import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, LocateFixed, Search } from "lucide-react";
import type { MapPin } from "@hub/shared";
import { Avatar, Button, EmptyState, IconButton, Input, Pill, Spinner, Tabs } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { useOpenContact } from "@/app/navigation";
import { hostMode } from "@/engine/transport";
import { Page } from "@/app/layout/Page";
import { SearchBox } from "@/features/people/SearchBox";
import { tierLabel, tierTone } from "@/features/people/tier";
import { distanceKm } from "./cluster";
import { ConnectionsGraph, ConnectionsList } from "./ConnectionsGraph";
import { StreetMap, type MapFocus } from "./StreetMap";

interface Place {
  name: string;
  latitude: number;
  longitude: number;
  pins: MapPin[];
}

/** One entry per city, whatever the zoom: the Places list and the city search read these. */
function placesOf(pins: MapPin[]): Place[] {
  const map = new Map<string, Place>();
  for (const p of pins) {
    const name = p.city?.trim() || "Somewhere";
    const cur = map.get(name);
    if (cur) {
      const n = cur.pins.length;
      cur.latitude = (cur.latitude * n + p.latitude) / (n + 1);
      cur.longitude = (cur.longitude * n + p.longitude) / (n + 1);
      cur.pins.push(p);
    } else map.set(name, { name, latitude: p.latitude, longitude: p.longitude, pins: [p] });
  }
  return [...map.values()].sort((a, b) => b.pins.length - a.pins.length || a.name.localeCompare(b.name));
}

interface Selection {
  title: string;
  pins: MapPin[];
  /** Distance from the user, when the selection came from Near me. */
  distances?: Map<string, number>;
}

type View = "places" | "connections";

export function MapPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "connections" ? "connections" : "places";
  const setView = (v: View) => setParams((p) => { const n = new URLSearchParams(p); if (v === "places") n.delete("view"); else n.set("view", v); return n; }, { replace: true });
  return (
    <Page flush before={<IconButton label="Back" onClick={() => navigate("/people")}><ArrowLeft size={16} /></IconButton>} title="Map" actions={<MapSummary view={view} />}>
      <div style={{ padding: "0 16px", borderBottom: "1px solid var(--border)" }}>
        <Tabs<View> value={view} onChange={setView} tabs={[{ id: "places", label: "Places" }, { id: "connections", label: "Connections" }]} />
      </div>
      {view === "places" ? <PlacesView /> : <ConnectionsView from={params.get("from")} setFrom={(id) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", "connections"); if (id) n.set("from", id); else n.delete("from"); return n; }, { replace: true })} />}
    </Page>
  );
}

function MapSummary({ view }: { view: View }) {
  const pins = useEngineQuery("getMapPins", {}, { enabled: view === "places" });
  // In a browser only cities and postal codes are looked up (placeLookups.ts), so only those count as waiting.
  const geo = useEngineQuery("getGeocodeStatus", { coarse: hostMode() === "web" }, { refetchInterval: 15_000, enabled: view === "places" });
  if (view !== "places") return null;
  return (
    <span className="u-small u-muted">
      {pins.data ? `${pins.data.length} people with a location` : ""}
      {geo.data && geo.data.pending > 0 ? `, locating ${geo.data.pending} more place${geo.data.pending === 1 ? "" : "s"}` : ""}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Places

function PlacesView() {
  const open = useOpenContact();
  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [selected, setSelected] = useState<Selection | null>(null);
  const [cityQuery, setCityQuery] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [me, setMe] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  // New positions arrive in the background while lookups run; look again now and then.
  const pins = useEngineQuery("getMapPins", { query: query || undefined }, { refetchInterval: 20_000 });
  const places = useMemo(() => placesOf(pins.data ?? []), [pins.data]);
  const selectedIds = useMemo(() => new Set((selected?.pins ?? []).map((p) => p.contactId)), [selected]);

  const goTo = (latitude: number, longitude: number, zoom: number) => setFocus({ key: Date.now(), latitude, longitude, zoom });
  const showPlace = (p: Place) => {
    setSelected({ title: p.name, pins: p.pins });
    goTo(p.latitude, p.longitude, 11);
  };
  const cityMatches = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    if (!q) return [];
    return places.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [cityQuery, places]);
  const jumpToCity = (p?: Place) => {
    const hit = p ?? cityMatches[0];
    if (!hit) return;
    setCityQuery(hit.name);
    setCityOpen(false);
    showPlace(hit);
  };
  const nearMe = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocateError("Location is not available here.");
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const here = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setMe(here);
        const distances = new Map<string, number>();
        const near = (pins.data ?? [])
          .map((p) => ({ p, km: distanceKm(here.latitude, here.longitude, p.latitude, p.longitude) }))
          .filter((x) => x.km <= 150)
          .sort((a, b) => a.km - b.km);
        for (const n of near) distances.set(n.p.contactId, n.km);
        setSelected({ title: near.length ? "Near you" : "Nobody within 150 km", pins: near.map((n) => n.p), distances });
        goTo(here.latitude, here.longitude, 8);
      },
      (err) => {
        setLocating(false);
        setLocateError(err.code === err.PERMISSION_DENIED ? "Location is not allowed. Turn it on for Hub in System Settings, Privacy and Security." : "Could not find where you are.");
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  };

  return (
    <>
      <div className="u-row" style={{ gap: 8, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
        <div style={{ position: "relative", width: 260 }}>
          <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-tertiary)", pointerEvents: "none" }} />
          <Input
            aria-label="Jump to a city"
            placeholder="Jump to a city"
            value={cityQuery}
            onChange={(e) => { setCityQuery(e.target.value); setCityOpen(true); }}
            onFocus={() => setCityOpen(true)}
            onBlur={() => setTimeout(() => setCityOpen(false), 150)}
            onKeyDown={(e) => { if (e.key === "Enter") jumpToCity(); if (e.key === "Escape") setCityOpen(false); }}
            style={{ paddingLeft: 28, width: "100%" }}
          />
          {cityOpen && cityQuery.trim() && (
            <div role="listbox" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, marginTop: 4, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
              {cityMatches.length === 0 && <div className="u-small u-muted" style={{ padding: "8px 10px" }}>Nobody lives in a place called that.</div>}
              {cityMatches.map((p) => (
                <button key={p.name} type="button" role="option" aria-selected={false} onMouseDown={(e) => e.preventDefault()} onClick={() => jumpToCity(p)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 8, padding: "7px 10px", border: "none", background: "transparent", cursor: "pointer", color: "inherit", textAlign: "left" }}>
                  <span className="u-grow u-truncate">{p.name}</span>
                  <span className="u-small u-muted">{p.pins.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={nearMe} disabled={locating} title="Centre the map on where you are"><LocateFixed size={14} /> {locating ? "Locating" : "Near me"}</Button>
        {locateError && <span className="u-small" style={{ color: "var(--danger)" }}>{locateError}</span>}
        <div style={{ flex: 1, minWidth: 220 }}>
          <SearchBox value={query} onChange={setQuery} showHints={false} placeholder="Filter who is on the map" />
        </div>
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, position: "relative", background: "var(--bg-sunken)" }}>
          {pins.isLoading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner /></div>}
          <StreetMap pins={pins.data ?? []} selectedIds={selectedIds} me={me} focus={focus} onOpen={open} onGroup={(group, title) => setSelected({ title, pins: group })} />
        </div>
        <aside style={{ width: 300, borderLeft: "1px solid var(--border)", overflowY: "auto", padding: 12 }}>
          {!selected && (
            <>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Places</div>
              {places.length === 0 && pins.isSuccess && <EmptyState title="No locations">Add a city to a person's profile and they appear here.</EmptyState>}
              {places.map((p) => (
                <button key={p.name} type="button" onClick={() => showPlace(p)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 8, padding: "6px 8px", border: "none", background: "transparent", cursor: "pointer", borderRadius: 6, color: "inherit", textAlign: "left" }}>
                  <span className="u-grow u-truncate">{p.name}</span>
                  <span className="u-small u-muted">{p.pins.length}</span>
                </button>
              ))}
            </>
          )}
          {selected && (
            <>
              <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>{selected.title}{selected.pins.length > 0 && !selected.title.startsWith(String(selected.pins.length)) ? ` (${selected.pins.length})` : ""}</span>
                <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setCityQuery(""); setFocus({ key: Date.now(), world: true }); }}>All places</Button>
              </div>
              {selected.pins.map((p) => (
                <button key={p.contactId} type="button" onClick={() => open(p.contactId)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 8, padding: "6px 8px", border: "none", background: "transparent", cursor: "pointer", borderRadius: 6, color: "inherit", textAlign: "left" }}>
                  <Avatar name={p.displayName} src={p.avatarUrl} size={26} seed={p.contactId} tier={p.bucket} />
                  <span className="u-grow" style={{ minWidth: 0 }}>
                    <span className="u-row" style={{ gap: 6 }}>
                      <span className="u-truncate">{p.displayName}</span>
                      {p.bucket < 4 && <Pill tone={tierTone(p.bucket)}>{tierLabel(p.bucket)}</Pill>}
                    </span>
                    {(selected.distances || (p.city && selected.title !== p.city)) && (
                      <span className="u-small u-muted" style={{ display: "block" }}>
                        {selected.distances ? `${Math.round(selected.distances.get(p.contactId) ?? 0)} km` : ""}{selected.distances && p.city ? ", " : ""}{p.city && selected.title !== p.city ? p.city : ""}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}
        </aside>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Connections

function ConnectionsView({ from, setFrom }: { from: string | null; setFrom: (id: string | null) => void }) {
  const self = useEngineQuery("getSelfContact", undefined);
  const [pick, setPick] = useState("");
  const [pickOpen, setPickOpen] = useState(false);
  const matches = useEngineQuery("queryContacts", { query: pick.trim(), limit: 8, offset: 0 }, { enabled: pick.trim().length > 0 });
  const centerId = from ?? self.data?.contact.id ?? null;
  const graph = useEngineQuery("getConnectionGraph", { contactId: centerId ?? "", limit: 24 }, { enabled: Boolean(centerId) });
  const [history, setHistory] = useState<string[]>([]);
  const recenter = (id: string) => {
    if (centerId) setHistory((h) => [...h.slice(-19), centerId]);
    setFrom(id);
  };
  const back = () => {
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFrom(prev && prev !== self.data?.contact.id ? prev : null);
  };
  return (
    <>
      <div className="u-row" style={{ gap: 8, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
        <div style={{ position: "relative", width: 280 }}>
          <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-tertiary)", pointerEvents: "none" }} />
          <Input aria-label="Start from someone" placeholder="Start from someone" value={pick} onChange={(e) => { setPick(e.target.value); setPickOpen(true); }} onFocus={() => setPickOpen(true)} onBlur={() => setTimeout(() => setPickOpen(false), 150)} onKeyDown={(e) => { if (e.key === "Escape") setPickOpen(false); }} style={{ paddingLeft: 28, width: "100%" }} />
          {pickOpen && pick.trim() && (
            <div role="listbox" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, marginTop: 4, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
              {matches.data?.items.length === 0 && <div className="u-small u-muted" style={{ padding: "8px 10px" }}>No one by that name.</div>}
              {(matches.data?.items ?? []).map((r) => (
                <button key={r.contactId} type="button" role="option" aria-selected={false} onMouseDown={(e) => e.preventDefault()} onClick={() => { recenter(r.contactId); setPick(""); setPickOpen(false); }} style={{ display: "flex", width: "100%", alignItems: "center", gap: 8, padding: "6px 10px", border: "none", background: "transparent", cursor: "pointer", color: "inherit", textAlign: "left" }}>
                  <Avatar name={r.displayName} src={r.avatarUrl} size={22} seed={r.contactId} tier={r.bucket} />
                  <span className="u-grow u-truncate">{r.displayName}</span>
                  {r.headline && <span className="u-small u-muted u-truncate" style={{ maxWidth: 120 }}>{r.headline}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {history.length > 0 && <Button size="sm" variant="ghost" onClick={back}>Back</Button>}
        {from && self.data && from !== self.data.contact.id && <Button size="sm" variant="outline" onClick={() => { setHistory([]); setFrom(null); }}>Back to you</Button>}
        {graph.data && <span className="u-small u-muted">{graph.data.isSelf ? "Your connections" : `${graph.data.center.displayName}'s connections`}: {graph.data.nodes.length + graph.data.more} {graph.data.nodes.length + graph.data.more === 1 ? "person" : "people"}</span>}
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!centerId ? (
            self.isLoading ? <Spinner /> : (
              <EmptyState title="Start from your own card">Set which person is you under Settings, Account, and this map starts from you. Or pick anyone above.</EmptyState>
            )
          ) : (
            <div style={{ width: "100%", maxWidth: 960 }}>
              <ConnectionsGraph contactId={centerId} limit={24} height={560} onCenter={recenter} />
            </div>
          )}
        </div>
        <aside style={{ width: 320, borderLeft: "1px solid var(--border)", overflowY: "auto", padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{graph.data ? (graph.data.isSelf ? "Who you know" : `Who ${graph.data.center.displayName} knows`) : "Connections"}</div>
          {graph.data && graph.data.nodes.length === 0 && <div className="u-small u-muted">Nothing shared yet.</div>}
          {graph.data && <ConnectionsList graph={graph.data} onCenter={recenter} />}
        </aside>
      </div>
    </>
  );
}
